// src/services/api.js — HorizonteCassaPyME
// Bridges Cassa POS to the HorizontePyME backend (same shared backend).
// Auth token injection is handled automatically by the axios interceptors
// registered in AuthProvider — no manual token headers needed here.

import axios from 'axios';
import { NODE_BACKEND_API_URL, NODE_BACKEND_PRD_URL, NODE_BACKEND_BRW_URL } from '../config';

// ---------------------------------------------------------------------------
// Payment method mapping: Cassa labels -> PyME numeric codes
// 201 Contado | 204 T.Credito | 205 T.Debito | 206 Transferencia
// 207 Mercado Pago | 208 Otra
// ---------------------------------------------------------------------------
const PAYMENT_METHOD_CODES = {
    efectivo:      '201',
    debito:        '205',
    credito:       '204',
    qr:            '207',
    transferencia: '206',
    rappi:         '207',
    pedidosya:     '208',
};

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function mapProduct(p) {
    return {
        id:          p.ProductId,
        name:        p.ProductDescription,
        price:       p.RetailPrice || 0,
        category:    p.CategoryName,
        brand:       p.BrandName,
        productCode: p.ProductCode,
        ean:         p.EAN,
        stock:       p.StockQuantity,
        _raw: p,
    };
}

// V2: backend fetches price from dbo.Prices — no client-side IVA calculation needed.
// isVolumePricingActive: false → backend ignores frontend RetailPrice for standard products.
function buildInvoiceItem(item) {
    const raw = item._raw || {};
    return {
        ProductId:            raw.ProductId    || item.id          || null,
        ProductDescription:   item.name,
        ProductCode:          raw.ProductCode  || item.productCode || '',
        EAN:                  raw.EAN          || item.ean         || '',
        BrandName:            raw.BrandName    || item.brand       || '',
        CategoryName:         raw.CategoryName || item.category    || '',
        quantity:             item.quantity,
        splitter:             1,
        StockQuantity:        raw.StockQuantity || item.stock || null,
        RetailPrice:          item.price || 0,  // fallback if product not in DB
        isVolumePricingActive: false,
    };
}

// ---------------------------------------------------------------------------
// ApiService
// ---------------------------------------------------------------------------

class ApiService {

    // GET /apitest/getcategorynames
    // Backend may return string[] OR [{CategoryName:"..."},...] — normalise to string[]
    async getCategories() {
        const response = await axios.get(`${NODE_BACKEND_API_URL}/getcategorynames`);
        const data = response.data || [];
        return data.map(item =>
            typeof item === 'string' ? item : (item.CategoryName || item.categoryName || String(item))
        );
    }

    // GET /api/v3/browse?categoryName=<cat>&includeStockedOnly=false
    // /browse does not require a search term — returns all products in the category
    async getProductsByCategory(category) {
        const response = await axios.get(NODE_BACKEND_BRW_URL, {
            params: { categoryName: category, includeStockedOnly: false },
        });
        return (response.data || []).map(mapProduct);
    }

    // GET /api/v3/search — requires a non-empty search term
    async searchProducts(searchTerm, additionalFilters = {}) {
        const term = (searchTerm || '').trim();
        if (!term) return [];
        const params = { search: term, includeStockedOnly: false, ...additionalFilters };
        const response = await axios.get(NODE_BACKEND_PRD_URL + 'search', { params });
        return (response.data || []).map(mapProduct);
    }

    async getProducts(filters = {}) {
        if (filters.search && filters.search.trim()) {
            return this.searchProducts(filters.search, filters);
        }
        return this.getProductsByCategory(filters.categoryName || '');
    }

    // POST /apitest/invoices/v2/create — Factura X / Presupuesto (no AFIP)
    // V2: backend recalculates totals from dbo.Prices — we don't send subtotal/total.
    // Future AFIP path: set registerWithAFIP:true + invoiceType:null + clientData with fiscal data.
    async createOrder(orderData) {
        // null paymentMethod for non-sale types (waste, stock adjustment)
        const paymentCode = orderData.paymentMethod
            ? (PAYMENT_METHOD_CODES[orderData.paymentMethod] || '201')
            : null;
        const discountPct = orderData.discount || 0;
        const ts = Date.now();

        const payload = {
            cartItems:          orderData.items.map(buildInvoiceItem),
            discount:           { percentage: discountPct, amount: 0 },
            invoiceType:        'X',
            registerWithAFIP:   false,
            paymentMethod:      paymentCode,
            clientData:         null,
            clientTaxCondition: 'CF',
            sessionId:          `cassa_${ts}`,
            customReference:    `CASSA-${ts}`,
            paymentMode:        'single',
            notes:              orderData.notes || null,
        };

        const response = await axios.post(
            `${NODE_BACKEND_API_URL}/invoices/v2/create`,
            payload,
            { timeout: 60000 },
        );
        return response.data;
    }

    // GET /apitest/invoices
    async getOrders(filters = {}) {
        const response = await axios.get(`${NODE_BACKEND_API_URL}/invoices`, { params: filters });
        return response.data;
    }

    async getOrder(invoiceId) {
        const response = await axios.get(`${NODE_BACKEND_API_URL}/invoices/${invoiceId}`);
        return response.data;
    }

    // GET /apitest/analytics/dashboard
    async getDashboardStats(filters = {}) {
        const response = await axios.get(`${NODE_BACKEND_API_URL}/analytics/dashboard`, { params: filters });
        return response.data;
    }

    // GET /apitest/analytics/v2/daily-sales-progress
    // Matches HorizontePyME "VENTAS HOY" card data
    async getDailySalesProgress() {
        const response = await axios.get(`${NODE_BACKEND_API_URL}/analytics/v2/daily-sales-progress`);
        return response.data;
    }

    // GET /apitest/analytics/average-invoice-value?date=YYYY-MM-DD
    // Matches HorizontePyME "Promedio por Factura" card data
    async getAverageInvoiceValue(date) {
        const params = date ? { date } : {};
        const response = await axios.get(`${NODE_BACKEND_API_URL}/analytics/average-invoice-value`, { params });
        return response.data;
    }

    // GET /apitest/analytics/best-sellers?count=10&period=day
    // Matches HorizontePyME "BESTSELLERS HOY" card data
    async getBestSellers(count = 10, period = 'day') {
        const response = await axios.get(`${NODE_BACKEND_API_URL}/analytics/best-sellers`, {
            params: { count, period },
        });
        return response.data;
    }

    // GET /apitest/analytics/top-products?period=day&count=5
    // Matches HorizontePyME "Bestsellers hoy" (TopProductsToday) card data
    async getTopProducts(period = 'day', count = 5) {
        const response = await axios.get(`${NODE_BACKEND_API_URL}/analytics/top-products`, {
            params: { period, count },
        });
        return response.data;
    }

    // GET /apitest/analytics/v2/sales-by-category?startDate=...&endDate=...
    // Matches HorizontePyME "VENTAS POR CATEGORÍA" card data
    async getSalesByCategoryV2(startDate, endDate) {
        const response = await axios.get(`${NODE_BACKEND_API_URL}/analytics/v2/sales-by-category`, {
            params: { startDate, endDate },
        });
        const data = response.data;
        if (data && data.data && Array.isArray(data.data.categories)) return data.data.categories;
        if (data && Array.isArray(data.categories)) return data.categories;
        if (Array.isArray(data)) return data;
        return [];
    }

    async getSalesByCategory(filters = {}) {
        const response = await axios.get(
            `${NODE_BACKEND_API_URL}/analytics/sales-by-category`,
            { params: filters },
        );
        const data = response.data;
        // Backend may return an array or an object wrapping the array
        if (Array.isArray(data)) return data;
        if (data && Array.isArray(data.data)) return data.data;
        if (data && Array.isArray(data.categories)) return data.categories;
        return [];
    }

    async getHourlySales() {
        const response = await axios.get(`${NODE_BACKEND_API_URL}/analytics/daily-sales-stats`);
        return response.data;
    }

    async getAvailableDates() {
        const response = await axios.get(`${NODE_BACKEND_API_URL}/analytics/invoice-dates`);
        return response.data;
    }
}

const apiService = new ApiService();
export default apiService;
